import React from "react";
import { Plus, Bookmark, Share2, Sparkles, Target, ChevronDown } from "lucide-react";

// OptionStrat-style strategy header bar — replaces the old Gamma Trade hero
// strip. Big strategy name + a "Build ▾" button to open the picker, then
// the ticker chip with live spot + Δ%, expirations tally, and the right-
// side actions (Recommend / Optimizer / Save / Share).
const ForgeHeaderBar = ({
  strategyName = "Pick a strategy",
  ticker = "SPY",
  spot,
  changePct,
  expirations = [],
  legCount = 0,
  netPremium = null,
  onOpenPicker,
  onOpenRecommend,
  onOpenOptimizer,
  onOpenSaved,
  onOpenShare,
  canShare = false,
}) => {
  const isUp = (changePct ?? 0) >= 0;
  const expSummary = expirations.length
    ? expirations.map((d) => `${d}d`).join(", ")
    : "—";

  return (
    <div
      className="bg-[#0B1426] border-b border-white/10"
      data-testid="forge-header-bar"
    >
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center gap-x-5 gap-y-2">
        {/* Strategy name → click to open picker */}
        <button
          type="button"
          onClick={onOpenPicker}
          data-testid="forge-open-picker"
          className="group inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-white/10 bg-[#0E1A30] hover:border-[#3B82F6]/50 hover:bg-[#142340] transition"
        >
          <span
            data-testid="forge-strategy-name"
            className="text-[20px] sm:text-[22px] font-medium tracking-tight text-white leading-none"
          >
            {strategyName}
          </span>
          <ChevronDown className="w-4 h-4 text-white/50 group-hover:text-[#3B82F6]" />
        </button>

        {/* Ticker + spot + Δ% */}
        <div className="flex items-center gap-2 text-white">
          <span className="text-[11px] uppercase tracking-[0.18em] text-white/50">{ticker}</span>
          <span className="num text-[18px] font-medium">
            {Number.isFinite(spot) ? Number(spot).toFixed(2) : "—"}
          </span>
          {Number.isFinite(changePct) && (
            <span
              className={`num text-[12px] px-1.5 py-0.5 rounded ${
                isUp ? "text-[#22C55E] bg-[#22C55E]/10" : "text-[#EF4444] bg-[#EF4444]/10"
              }`}
            >
              {isUp ? "+" : ""}{Number(changePct).toFixed(2)}%
            </span>
          )}
        </div>

        {/* Expirations chip */}
        <div className="text-[11px] uppercase tracking-[0.18em] text-white/45">
          Expirations:&nbsp;
          <span className="text-white/80 normal-case tracking-normal num">{expSummary}</span>
        </div>

        {/* Net premium */}
        {netPremium !== null && Number.isFinite(netPremium) && (
          <div className="text-[11px] uppercase tracking-[0.18em] text-white/45">
            {netPremium >= 0 ? "Net debit" : "Net credit"}:&nbsp;
            <span className={`num normal-case tracking-normal ${
              netPremium >= 0 ? "text-[#EF4444]" : "text-[#22C55E]"
            }`}>
              ${Math.abs(netPremium).toFixed(2)}
            </span>
          </div>
        )}

        {/* Right-side actions */}
        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          <button
            type="button"
            onClick={onOpenPicker}
            data-testid="forge-add-leg-btn"
            className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-md bg-[#3B82F6] hover:bg-[#2563EB] text-white transition font-medium"
          >
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
          <button
            type="button"
            onClick={onOpenRecommend}
            data-testid="forge-recommend-btn"
            className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-md border border-white/15 text-white/80 hover:text-white hover:border-white/35 transition"
          >
            <Sparkles className="w-3.5 h-3.5" /> Recommend
          </button>
          <button
            type="button"
            onClick={onOpenOptimizer}
            data-testid="forge-optimizer-btn"
            className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-md border border-white/15 text-white/80 hover:text-white hover:border-white/35 transition"
          >
            <Target className="w-3.5 h-3.5" /> Optimizer
          </button>
          <button
            type="button"
            onClick={onOpenSaved}
            data-testid="forge-saved-btn"
            className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-md border border-white/15 text-white/80 hover:text-white hover:border-white/35 transition"
          >
            <Bookmark className="w-3.5 h-3.5" />
            Saved {legCount > 0 ? `(${legCount})` : ""}
          </button>
          <button
            type="button"
            onClick={onOpenShare}
            disabled={!canShare}
            data-testid="forge-share-btn"
            className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-md border border-white/15 text-white/80 hover:text-white hover:border-white/35 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgeHeaderBar;
